import { useState } from "react"

import {
  Check,
  ClipboardCopy,
  MessageSquareQuote,
  Presentation,
  RefreshCw,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

const sectionLabels = ["The Challenge", "The Solution", "Business Impact", "Why Now"]
const sectionColors = [
  "border-l-destructive bg-destructive/10",
  "border-l-success bg-success/10",
  "border-l-primary bg-primary/15",
  "border-l-warning bg-warning/10",
]

function PitchParagraphs({ text }: { text: string }) {
  const paragraphs = text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)

  if (paragraphs.length >= 3) {
    return (
      <div className="space-y-3">
        {paragraphs.map((p, i) => {
          const label = sectionLabels[i] ?? null
          const color = sectionColors[i % sectionColors.length]
          return (
            <div key={i} className={cn("rounded-lg border-l-[3px] px-4 py-3", color)}>
              {label && (
                <span className="text-muted-foreground mb-1 block text-[10px] font-bold tracking-wider uppercase">
                  {label}
                </span>
              )}
              <p className="text-sm leading-relaxed">{p}</p>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="border-l-primary/40 rounded-lg border-l-[3px] px-4 py-3">
      <p className="text-sm leading-relaxed whitespace-pre-wrap">{text}</p>
    </div>
  )
}

type SalesPitchDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  isLoading: boolean
  result: string | null
  onRegenerate: () => void
}

export function SalesPitchDialog({
  open,
  onOpenChange,
  isLoading,
  result,
  onRegenerate,
}: SalesPitchDialogProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Presentation className="size-5" />
            Sales Pitch
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading && (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <Spinner className="size-8" />
              <div className="text-center">
                <p className="text-sm font-medium">Crafting a compelling pitch...</p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Analyzing your use case data to build a persuasive narrative
                </p>
              </div>
            </div>
          )}
          {!isLoading && result && (
            <div className="space-y-4">
              <div className="flex items-start gap-2">
                <MessageSquareQuote className="text-muted-foreground/30 mt-0.5 size-5 shrink-0" />
                <p className="text-muted-foreground text-xs italic">
                  AI-generated pitch based on your use case data
                </p>
              </div>
              <PitchParagraphs text={result} />
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button disabled={isLoading} size="sm" variant="outline" onClick={onRegenerate}>
            <RefreshCw className="mr-1 size-4" />
            Regenerate
          </Button>
          <Button disabled={isLoading || !result} size="sm" variant="outline" onClick={handleCopy}>
            {copied ? <Check className="mr-1 size-4" /> : <ClipboardCopy className="mr-1 size-4" />}
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
